import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ChevronDown, FileText, Plus } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  get_cms_document_by_id,
  get_cms_document_versions,
  publish_cms_documents,
} from "../../reduxData/cms/cmsAction";
import { Badge, ConfirmModal, PageHeader } from "./CmsDocumentComponents.js";
import {
  apiError,
  formatVersion,
  getDocumentHtmlContent,
  humanize,
  sanitizeHtml,
} from "./cmsDocumentUtils";
import { formatDateTime } from "../../hooks/formatDateTime";
import "../../css/cmsDocuments.css";

export default function VersionHistory() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const document = useSelector((state) => state.cms.cmsDocument);
  const versions = useSelector((state) => state.cms.cmsDocumentVersions);
  const [open, setOpen] = useState([]);
  const [full, setFull] = useState([]);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const [, list] = await Promise.all([
        get_cms_document_by_id(id, dispatch),
        get_cms_document_versions(id, dispatch),
      ]);
      const sorted = [...list].sort(
        (a, b) => Number(b.version) - Number(a.version),
      );
      const preferred = sorted.find((v) => v.isCurrent) ?? sorted[0];
      setOpen(preferred ? [String(preferred.id ?? preferred.version)] : []);
    } catch (e) {
      setError(apiError(e, t("cmsDocuments.errors.loadHistory")));
    }
  }, [dispatch, id]);
  useEffect(() => {
    load();
  }, [load]);
  const currentVersion = useMemo(
    () =>
      versions.find((v) => v.isCurrent) ??
      versions.find((v) => Number(v.version) === Number(document?.version)),
    [document, versions],
  );
  const toggle = (key) =>
    setOpen((value) =>
      value.includes(key) ? value.filter((v) => v !== key) : [...value, key],
    );
  const act = async () => {
    setBusy(true);
    try {
      await publish_cms_documents(confirm.item.id, dispatch);
      toast.success(t("cmsDocuments.messages.versionPublished"));
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(apiError(e, t("cmsDocuments.errors.updateVersion")));
    } finally {
      setBusy(false);
    }
  };
  if (error)
    return (
      <main className="cmsd-page">
        <div className="cmsd-state">
          <h2>{t("cmsDocuments.errors.loadHistoryTitle")}</h2>
          <p>{error}</p>
        </div>
      </main>
    );
  return (
    <main className="cmsd-page">
      <PageHeader
        title={t("cmsDocuments.history.title")}
        description={
          document
            ? `${document.title} · ${humanize(document.type)} · ${document.language} · ${humanize(document.visibility)}`
            : t("cmsDocuments.history.loading")
        }
      >
        <Link className="btn btn-light" to="/cms/documents">
          <ArrowLeft size={17} /> {t("cmsDocuments.actions.backToDocuments")}
        </Link>
        <Link className="btn btn-light" to={`/cms/documents/${id}`}>
          <FileText size={17} /> {t("cmsDocuments.history.currentDocument")}
        </Link>
        <Link
          className="btn cmsd-primary"
          to={`/cms/documents/${id}/new-version`}
        >
          <Plus size={17} /> {t("cmsDocuments.actions.addNewVersion")}
        </Link>
      </PageHeader>
      {versions.length > 1 && (
        <div className="cmsd-expand-actions">
          <button
            onClick={() =>
              setOpen(versions.map((v) => String(v.id ?? v.version)))
            }
          >
            {t("cmsDocuments.history.expandAll")}
          </button>
          <button onClick={() => setOpen([])}>{t("cmsDocuments.history.collapseAll")}</button>
        </div>
      )}
      {versions.length === 0 && document && (
        <div className="cmsd-state">
          <h2>{t("cmsDocuments.history.emptyTitle")}</h2>
          <p>{t("cmsDocuments.history.emptyDescription")}</p>
        </div>
      )}
      <section className="cmsd-versions">
        {versions.map((version) => {
          const key = String(version.id ?? version.version);
          const expanded = open.includes(key);
          const isCurrent = version === currentVersion;
          return (
            <article className="cmsd-version-card" key={key}>
              <button
                className="cmsd-version-head"
                onClick={() => toggle(key)}
                aria-expanded={expanded}
              >
                <span className="cmsd-version-number">
                  {formatVersion(version.version)}
                </span>
                <div>
                  <strong>
                    {isCurrent
                      ? t("cmsDocuments.history.currentPublishedVersion")
                      : t(`cmsDocuments.values.${String(
                          version.status === "PUBLISHED"
                            ? "SUPERSEDED"
                            : version.status,
                        ).toLowerCase()}`, { defaultValue: humanize(version.status) })}
                  </strong>
                  <small>
                    {version.publishedAt
                      ? t("cmsDocuments.history.publishedOn", { date: formatDateTime(version.publishedAt, i18n, false) })
                      : t("cmsDocuments.history.createdOn", { date: formatDateTime(version.createdAt, i18n, false) })}
                    {version.publishedBy
                      ? ` · ${typeof version.publishedBy === "object" ? version.publishedBy.name : version.publishedBy}`
                      : ""}
                  </small>
                </div>
                <div className="ms-auto d-flex align-items-center gap-2">
                  {isCurrent && <span className="cmsd-current">{t("cmsDocuments.history.current")}</span>}
                  <Badge value={version.status} />
                  <ChevronDown
                    className={
                      expanded ? "cmsd-chevron is-open" : "cmsd-chevron"
                    }
                  />
                </div>
              </button>
              {expanded && (
                <div className="cmsd-version-body">
                  <dl className="cmsd-metadata">
                    <Meta label={t("cmsDocuments.table.title")} value={version.title} />
                    <Meta label={t("cmsDocuments.table.type")} value={humanize(version.type)} />
                    <Meta label={t("cmsDocuments.history.language")} value={version.language} />
                    <Meta
                      label={t("cmsDocuments.table.visibility")}
                      value={humanize(version.visibility)}
                    />
                    <Meta
                      label={t("cmsDocuments.table.createdAt")}
                      value={formatDateTime(version.createdAt, i18n, false)}
                    />
                    <Meta
                      label={t("cmsDocuments.table.publishedAt")}
                      value={formatDateTime(version.publishedAt, i18n, false)}
                    />
                    <Meta
                      label={t("cmsDocuments.history.changeSummary")}
                      value={
                        version.changeSummary || t("cmsDocuments.history.noChangeSummary")
                      }
                    />
                  </dl>
                  <div
                    className={`cmsd-document-preview cmsd-version-preview ${full.includes(key) ? "is-full" : ""}`}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(getDocumentHtmlContent(version)),
                    }}
                  />
                  <div className="cmsd-version-actions">
                    <button
                      className="btn btn-light"
                      onClick={() =>
                        setFull((values) =>
                          values.includes(key)
                            ? values.filter((v) => v !== key)
                            : [...values, key],
                        )
                      }
                    >
                      {t(full.includes(key) ? "cmsDocuments.history.showLess" : "cmsDocuments.history.showFullContent")}
                    </button>
                    {version.status === "DRAFT" && (
                      <button
                        className="btn cmsd-primary"
                        onClick={() =>
                          setConfirm({ action: "publish", item: version })
                        }
                      >
                        {t("cmsDocuments.actions.publish")}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </section>
      <ConfirmModal
        action={confirm?.action}
        item={confirm?.item}
        busy={busy}
        onClose={() => setConfirm(null)}
        onConfirm={act}
      />
    </main>
  );
}
const Meta = ({ label, value }) => (
  <div>
    <dt>{label}</dt>
    <dd>{value || "—"}</dd>
  </div>
);
